import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import type { GenderEnum, TeamFiltersModel } from "shared/types";

import { Checkbox, Text } from "shared/uikit";
import { ButtonSizesEnum, TextStylesEnum, TextVariantsEnum } from "shared/uikit/types";
import { getGenderTitle } from "utils/serviceUtils/helpers";

import FilterPopover from "../FilterPopover";
import { FilterGrid } from "../FilterPopover/styles";

interface EnterCompetitionFilterProps {
	filters?: TeamFiltersModel;
	onChange?: ({ ages, genders, levels }: { ages: string[]; genders: GenderEnum[]; levels: string[] }) => void;
	btnClassName?: string;
}

const EnterCompetitionFilter: FC<EnterCompetitionFilterProps> = ({ filters, onChange, btnClassName }) => {
	const { t } = useTranslation();

	const [checkableFilters, setCheckableFilters] = useState({
		ages: (filters?.ages || []).map(x => ({ ...x, selected: false })),
		genders: (filters?.genders || []).map(x => ({ ...x, selected: false })),
		levels: (filters?.levels || []).map(x => ({ ...x, selected: false }))
	});

	const applyFilters = useCallback(() => {
		onChange &&
			onChange({
				ages: checkableFilters.ages.filter(x => x.selected).map(x => `${x.type.charAt(0)}${x.value}`),
				genders: checkableFilters.genders.filter(x => x.selected).map(x => x.value),
				levels: checkableFilters.levels.filter(x => x.selected).map(x => `${x.value}`)
			});
	}, [onChange, checkableFilters]);

	const clearFilters = useCallback(() => {
		setCheckableFilters(items => ({
			ages: items.ages.map(x => ({ ...x, selected: false })),
			genders: items.genders.map(x => ({ ...x, selected: false })),
			levels: items.levels.map(x => ({ ...x, selected: false }))
		}));

		onChange &&
			onChange({
				ages: [],
				genders: [],
				levels: []
			});
	}, [onChange]);

	const isFilterActive = useMemo(
		() =>
			!!checkableFilters?.ages?.filter(x => x.selected)?.length ||
			!!checkableFilters?.genders?.filter(x => x.selected)?.length ||
			!!checkableFilters?.levels?.filter(x => x.selected)?.length,
		[checkableFilters]
	);

	useEffect(() => {
		setCheckableFilters({
			ages: (filters?.ages || []).map(x => ({ ...x, selected: false })),
			genders: (filters?.genders || []).map(x => ({ ...x, selected: false })),
			levels: (filters?.levels || []).map(x => ({ ...x, selected: false }))
		});
	}, [filters]);

	const toggleFilter = (selected: boolean, type: "age" | "gender" | "level", value: number | GenderEnum | null) => {
		if (type === "age") {
			setCheckableFilters({
				...checkableFilters,
				ages: checkableFilters.ages.map(x => ({ ...x, selected: x.value === value ? selected : x.selected }))
			});
		}

		if (type === "gender") {
			setCheckableFilters({
				...checkableFilters,
				genders: checkableFilters.genders.map(x => ({ ...x, selected: x.value === value ? selected : x.selected }))
			});
		}

		if (type === "level") {
			setCheckableFilters({
				...checkableFilters,
				levels: checkableFilters.levels.map(x => ({ ...x, selected: x.value === value ? selected : x.selected }))
			});
		}
	};

	return (
		<FilterPopover
			onApplyFilters={applyFilters}
			onClearFilters={clearFilters}
			isFilterActive={isFilterActive}
			btnClassName={btnClassName}
		>
			<FilterGrid className="mt-2 grid gap-2">
				<div>
					<Text variants={TextVariantsEnum.Caption3} style={TextStylesEnum.Hint}>
						Age
					</Text>
					<div className="flex flex-col mt-1">
						{(checkableFilters.ages || []).map(item => (
							<Checkbox
								key={item.value}
								wrapperSize={ButtonSizesEnum.SM}
								label={`${item.value}`}
								value={item.selected}
								onChange={checked => toggleFilter(checked, "age", item.value)}
							/>
						))}
					</div>
				</div>
				<div>
					<Text variants={TextVariantsEnum.Caption3} style={TextStylesEnum.Hint}>
						Gender
					</Text>
					<div className="flex flex-col mt-1">
						{(checkableFilters.genders || []).map(item => (
							<Checkbox
								key={item.value}
								wrapperSize={ButtonSizesEnum.SM}
								label={getGenderTitle(item.value, t)}
								value={item.selected}
								onChange={checked => toggleFilter(checked, "gender", item.value)}
							/>
						))}
					</div>
				</div>
				<div>
					<Text variants={TextVariantsEnum.Caption3} style={TextStylesEnum.Hint}>
						Level
					</Text>
					<div className="flex flex-col mt-1">
						{(checkableFilters.levels || []).map(item => (
							<Checkbox
								key={item.value}
								wrapperSize={ButtonSizesEnum.SM}
								label={`${item.value + 1}`}
								value={item.selected}
								onChange={checked => toggleFilter(checked, "level", item.value)}
							/>
						))}
					</div>
				</div>
			</FilterGrid>
		</FilterPopover>
	);
};

export default EnterCompetitionFilter;
